/**
 * CandidateNameLink Component
 *
 * A reusable component for displaying candidate names as clickable links
 * that navigate to the candidate profile page. Handles various input formats
 * and preserves exact styling behavior when no candidate ID is available.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { formatFullName } from '@/lib/utils';
import { cn } from '@/lib/utils';

interface CandidateNameLinkProps {
  // Option 1: Pass candidate object
  candidate?: {
    id?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  
  // Option 2: Pass id and name separately
  candidateId?: string | null;
  candidateName?: string | null;
  
  // Option 3: Pass formatted name directly
  name?: string;
  
  // Styling
  className?: string;
  variant?: 'default' | 'medium' | 'semibold' | 'small';
  
  // Element type for fallback (when no id)
  // 'p' | 'span' | 'text' (text = no wrapper, just the text)
  as?: 'p' | 'span' | 'text';
  
  // Behavior
  stopPropagation?: boolean;
  openInNewTab?: boolean;
  
  // Fallback when no name at all (preserves current behavior)
  noNameFallback?: string | React.ReactNode;
}

export const CandidateNameLink: React.FC<CandidateNameLinkProps> = ({
  candidate,
  candidateId,
  candidateName,
  name,
  className,
  variant = 'default',
  as = 'text',
  stopPropagation = true,
  openInNewTab = false,
  noNameFallback,
}) => {
  // Determine the ID
  const id = candidate?.id || candidateId || null;
  
  // Determine the name
  let displayName: string | null = null;
  if (name) {
    displayName = name;
  } else if (candidateName) {
    displayName = candidateName;
  } else if (candidate) {
    displayName = formatFullName(candidate.first_name, candidate.last_name);
  }
  
  // If no name at all, use fallback
  if (!displayName) {
    if (noNameFallback !== undefined) {
      return <>{noNameFallback}</>;
    }
    return null;
  }
  
  // Base classes for link
  const baseLinkClasses = 'text-primary hover:underline';
  
  // Variant classes
  const variantClasses = {
    default: '',
    medium: 'font-medium',
    semibold: 'font-semibold',
    small: 'text-sm',
  };
  
  const linkClasses = cn(baseLinkClasses, variantClasses[variant], className);
  
  // If no ID, render fallback with same styling (preserving current behavior)
  if (!id) {
    // Remove hover:underline from fallback classes
    const fallbackBaseClasses = 'text-primary';
    const fallbackClasses = cn(
      fallbackBaseClasses,
      variantClasses[variant],
      className?.replace('hover:underline', '').trim()
    );
    
    switch (as) {
      case 'p':
        return <p className={fallbackClasses}>{displayName}</p>;
      case 'span':
        return <span className={fallbackClasses}>{displayName}</span>;
      case 'text':
      default:
        return <>{displayName}</>;
    }
  }
  
  // Render link
  return (
    <Link
      to={`/candidates/${id}`}
      className={linkClasses}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
    >
      {displayName}
    </Link>
  );
};
